import { LoadingState } from '@/components/primitives/LoadingState'
import { PageHeader } from '@/components/primitives/PageHeader'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  type Flag,
  fetchChangesToday,
  fetchFlagHistory,
  fetchFlags,
  fetchScheduledFlags,
  patchFlag
} from '@/lib/p6'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { AlertTriangle, Calendar, History, Rocket, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

export function SuperPowersPage() {
  const [search, setSearch] = useState('')
  const [pendingFlag, setPendingFlag] = useState<{
    flag: Flag
    nextValue: boolean
  } | null>(null)
  const [historyKey, setHistoryKey] = useState<string | null>(null)

  const flags = useQuery({
    queryKey: ['super-flags'],
    queryFn: fetchFlags,
    staleTime: 30_000
  })
  const changesToday = useQuery({
    queryKey: ['super-flags-changes-today'],
    queryFn: fetchChangesToday,
    refetchInterval: 60_000
  })
  const scheduled = useQuery({
    queryKey: ['super-flags-scheduled'],
    queryFn: fetchScheduledFlags,
    refetchInterval: 60_000
  })

  const history = useQuery({
    queryKey: ['super-flag-history', historyKey],
    queryFn: () => fetchFlagHistory(historyKey!),
    enabled: Boolean(historyKey)
  })

  const flip = useMutation({
    mutationFn: ({ flag, nextValue }: { flag: Flag; nextValue: boolean }) =>
      patchFlag(flag.key, nextValue),
    onSuccess: (_d, vars) => {
      toast.success(`${vars.flag.label}: ${vars.nextValue ? 'ON' : 'OFF'}`)
      queryClient.invalidateQueries({ queryKey: ['super-flags'] })
      queryClient.invalidateQueries({ queryKey: ['super-flags-changes-today'] })
      setPendingFlag(null)
    },
    onError: (err) => notifyError('Could not change flag', err)
  })

  const filteredGroups = useMemo(() => {
    if (!flags.data) return {}
    const term = search.trim().toLowerCase()
    if (!term) return flags.data.groups
    const out: Record<string, Flag[]> = {}
    for (const [g, list] of Object.entries(flags.data.groups)) {
      const matches = list.filter(
        (f) =>
          f.key.toLowerCase().includes(term) ||
          f.label.toLowerCase().includes(term) ||
          f.description.toLowerCase().includes(term) ||
          g.toLowerCase().includes(term)
      )
      if (matches.length > 0) out[g] = matches
    }
    return out
  }, [flags.data, search])

  function requestFlip(flag: Flag, nextValue: boolean) {
    if (flag.destructive) {
      setPendingFlag({ flag, nextValue })
    } else {
      flip.mutate({ flag, nextValue })
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Super Powers"
        description="The giant flags page. Every toggle on the platform — search, flip, history, schedule."
      />

      <Card className="rounded-2xl p-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[260px] flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search flags by name, group, or description…"
              className="pl-8"
            />
          </div>
          <Badge variant="secondary" className="ml-auto tabular-nums">
            {flags.data?.total ?? 0} flags
          </Badge>
        </div>
      </Card>

      {/* Today's changes + Scheduled — sticky strip */}
      <div className="grid gap-3 lg:grid-cols-2">
        <Card className="rounded-2xl p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <History className="size-4 text-primary" /> Changes today
          </div>
          {changesToday.isLoading ? (
            <LoadingState rows={2} />
          ) : (changesToday.data?.results ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No flag changes yet today.
            </p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {changesToday.data!.results.slice(0, 5).map((c) => (
                <li key={c.id} className="flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">
                    {c.setting}
                  </span>
                  <span>:</span>
                  <span>{JSON.stringify(c.after[c.setting])}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {c.actor} · {new Date(c.created_at).toLocaleTimeString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="rounded-2xl p-4">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold">
            <Calendar className="size-4 text-primary" /> Scheduled changes
          </div>
          {scheduled.isLoading ? (
            <LoadingState rows={2} />
          ) : (scheduled.data?.results ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing scheduled.</p>
          ) : (
            <ul className="space-y-1.5 text-sm">
              {scheduled.data!.results.map((s) => (
                <li key={s.id} className="flex items-center gap-2">
                  <span className="font-mono text-xs">{s.setting}</span>
                  <span>→ {JSON.stringify(s.target_value)}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {new Date(s.run_at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Giant accordion of flag groups */}
      {flags.isLoading ? (
        <LoadingState rows={8} />
      ) : (
        <Accordion
          type="multiple"
          defaultValue={Object.keys(filteredGroups).slice(0, 2)}
          className="space-y-3"
        >
          {Object.entries(filteredGroups).map(([group, list]) => (
            <Card key={group} className="rounded-2xl">
              <AccordionItem value={group} className="border-0">
                <AccordionTrigger className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Rocket className="size-4 text-primary" />
                    <span className="text-base font-semibold">{group}</span>
                    <Badge variant="secondary" className="tabular-nums">
                      {list.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-2 pb-2">
                  <div className="grid gap-1">
                    {list.map((f) => (
                      <FlagRow
                        key={f.key}
                        flag={f}
                        onFlip={(v) => requestFlip(f, v)}
                        onShowHistory={() => setHistoryKey(f.key)}
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Card>
          ))}
        </Accordion>
      )}

      {/* Destructive confirm */}
      <AlertDialog
        open={pendingFlag !== null}
        onOpenChange={(v) => !v && setPendingFlag(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="size-5 text-amber-600" />
              {pendingFlag?.nextValue ? 'Turn ON' : 'Turn OFF'}{' '}
              <span className="font-mono">{pendingFlag?.flag.label}</span>?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingFlag?.flag.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={flip.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={flip.isPending}
              onClick={() => pendingFlag && flip.mutate(pendingFlag)}
              className={
                pendingFlag?.flag.destructive
                  ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                  : ''
              }
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* History drawer (simple modal) */}
      <AlertDialog
        open={historyKey !== null}
        onOpenChange={(v) => !v && setHistoryKey(null)}
      >
        <AlertDialogContent className="max-w-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <History className="size-4" /> History ·{' '}
              <code className="text-sm">{historyKey}</code>
            </AlertDialogTitle>
            <AlertDialogDescription>
              Last 50 changes for this flag.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-[50vh] overflow-y-auto">
            {history.isLoading ? (
              <LoadingState rows={3} />
            ) : (
              <ul className="space-y-1.5 text-sm">
                {(history.data?.results ?? []).map((c) => (
                  <li
                    key={c.id}
                    className="flex flex-wrap items-center gap-2 border-b py-1.5"
                  >
                    <Badge variant="secondary">{c.actor_type}</Badge>
                    <span className="font-medium">{c.actor ?? '—'}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="font-mono text-xs">
                      {JSON.stringify(c.before)} → {JSON.stringify(c.after)}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {new Date(c.created_at).toLocaleString()}
                    </span>
                  </li>
                ))}
                {(history.data?.results ?? []).length === 0 && (
                  <li className="text-sm text-muted-foreground">
                    No changes recorded yet.
                  </li>
                )}
              </ul>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Close</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function FlagRow({
  flag,
  onFlip,
  onShowHistory
}: {
  flag: Flag
  onFlip: (v: boolean) => void
  onShowHistory: () => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -2 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.12 }}
      className={cn(
        'flex items-center gap-3 rounded-md px-2 py-2 transition-colors hover:bg-muted/40',
        flag.destructive && 'border-l-2 border-amber-500/60 pl-2.5'
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{flag.label}</span>
          {flag.destructive && (
            <Badge
              variant="outline"
              className="border-amber-500/30 text-[10px] text-amber-700"
            >
              destructive
            </Badge>
          )}
          <code className="ml-auto text-[10px] text-muted-foreground">
            {flag.key}
          </code>
        </div>
        {flag.description && (
          <p className="text-xs text-muted-foreground">{flag.description}</p>
        )}
        {flag.last_changed_at && (
          <p className="text-[10px] text-muted-foreground">
            Last changed {new Date(flag.last_changed_at).toLocaleString()}{' '}
            {flag.last_changed_by ? `by ${flag.last_changed_by}` : ''}
          </p>
        )}
      </div>
      <Button
        size="sm"
        variant="ghost"
        onClick={onShowHistory}
        className="h-7 text-xs"
      >
        History
      </Button>
      <Switch checked={flag.value} onCheckedChange={(v) => onFlip(v)} />
    </motion.div>
  )
}
